import React, { useState, useEffect } from 'react';
import SharedHeading from '../Shared/Heading/Heading';
import { Props_SnippetCountdown } from './Countdown.props';
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}


const SnippetCountdown = (props: Props_SnippetCountdown) => {
    const {
        heading,
        targetDateTime,
        expiredMessage
    } = props;
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  const headingLevel = heading.level?.value as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' || 'h2';

  useEffect(() => {
    const calculateTimeLeft = (): TimeLeft => {
      const targetValue = targetDateTime.targetDateTime?.value ?? '';
      
      // Handle empty or invalid date
      if (!targetValue || typeof targetValue !== 'string') {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      const targetTime = new Date(targetValue).getTime();
      
      // Check if date is valid
      if (isNaN(targetTime)) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      const currentTime = new Date().getTime();
      const timeDifference = targetTime - currentTime;

      if (timeDifference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    // Initial calculation
    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft);

    // Don't set up interval in test environment to avoid act() warnings
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'test') {
      return;
    }

    // Set up interval to update every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
    }, 1000);

    // Cleanup interval on component unmount
    return () => {
      if (typeof window !== 'undefined' && window.clearInterval) {
        window.clearInterval(timer);
      } else if (typeof clearInterval !== 'undefined') {
        clearInterval(timer);
      }
    };
  }, [targetDateTime]);

  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  return (
    <section className={`snippet-countdown ${isExpired ? 'snippet-countdown--expired' : ''}`}>
      <div className="snippet-countdown__container">
        <div className="snippet-countdown__heading-section">
          <SharedHeading
              level={headingLevel}
              alignment={
                heading.alignment?.value === "start" ||
                heading.alignment?.value === "center" ||
                heading.alignment?.value === "end"
                  ? heading.alignment.value
                  : undefined
              }
              visibility={typeof heading.visibility?.value === 'boolean' ? heading.visibility.value : heading.visibility?.value === 'true'}
              className="snippet-countdown__heading"
            >
              {heading.text?.value}
          </SharedHeading>

          {isExpired && expiredMessage?.expiredText?.value && typeof expiredMessage.expiredText.value === 'string' && (
            <div className="snippet-countdown__expired-message">
              <div>
               {CompileMarkdown(expiredMessage.expiredText.value)}
              </div>
            </div>
          )}

        </div>

        <div className="snippet-countdown__inner-container">
            <div className="snippet-countdown__timer">
              <div className="snippet-countdown__values">
                <span className="snippet-countdown__value num-days">{formatNumber(timeLeft.days)}</span>
                <span className='snippet-countdown__label'>Days</span>
              </div>
              <div className="snippet-countdown__values">
                <span className="snippet-countdown__value num-hours">{formatNumber(timeLeft.hours)}</span>
                <span className='snippet-countdown__label'>Hours</span>
              </div>
              <div className="snippet-countdown__values">
                <span className="snippet-countdown__value num-minutes">{formatNumber(timeLeft.minutes)}</span>
                <span className='snippet-countdown__label'>Minutes</span>
              </div>
              <div className="snippet-countdown__values">
                <span className="snippet-countdown__value num-seconds">{formatNumber(timeLeft.seconds)}</span>
                <span className='snippet-countdown__label'>Seconds</span>
              </div>
            </div>         
        </div>
      </div>
    </section>
  );
};

export default SnippetCountdown;